import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useViewDensity from "../../hooks/useViewDensity";
import { ENABLE_JO_REGISTRY } from "../../utils/featureFlags";
import ChangePasswordModal from "../modals/ChangePasswordModal";
import HeaderClock from "./HeaderClock";
import { APP_VERSION } from "../../utils/version";

import { useState, useRef, useEffect } from "react";
import NotificationBell from "./NotificationBell";

const DENSITY_LABELS = {
  normal: "Normal",
  compact: "Compact",
  dense: "Dense",
  auto: "Auto",
};

export default function Navbar() {
  const { user, logout, canAccessDashboard, canAccessNiDashboard, canAccessManageAccounts, canAccessJoRegistry, canAccessXlsx, canViewRoles, canAccessDatabaseBackups, canViewLoginActivity, canAccessReports, canAccessMigration, canAccessMigrationDashboard, canAccessPublicMigrationRequests, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [niOpen, setNiOpen] = useState(false);
  const [densityOpen, setDensityOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef(null);
  const migrationRef = useRef(null);
  const niRef = useRef(null);
  const densityRef = useRef(null);
  const { density, setDensity } = useViewDensity();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (migrationRef.current && !migrationRef.current.contains(event.target)) {
        setMigrationOpen(false);
      }
      if (niRef.current && !niRef.current.contains(event.target)) {
        setNiOpen(false);
      }
      if (densityRef.current && !densityRef.current.contains(event.target)) {
        setDensityOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navigateTo = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <>
    <nav className="bg-blue-700 text-white shadow relative z-50">
      <div className="w-full px-4 lg:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded overflow-hidden flex-shrink-0 bg-white/10">
            <img src="/best-icon.png" alt="BEST Inc." className="h-full w-full object-contain" />
          </div>
          <span className="text-lg font-semibold text-white">
            BEST Dispatch Monitoring System
          </span>
        </div>

        <HeaderClock />

        <div className="flex items-center gap-2">
          {(canAccessNiDashboard || canAccessDashboard || canAccessXlsx) && (
            <div className="relative" ref={niRef}>
              <button
                onClick={() => setNiOpen((o) => !o)}
                className="bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm inline-flex items-center gap-1"
              >
                New Installation
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {niOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white text-black rounded shadow-lg overflow-hidden z-50">
                  {canAccessNiDashboard && (
                    <button onClick={() => { setNiOpen(false); navigate("/ni/dashboard"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm font-medium">NI Dashboard</button>
                  )}
                  {canAccessXlsx && (
                    <button onClick={() => { setNiOpen(false); navigate("/xlsx-imports"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">BO Imports</button>
                  )}
                  {canAccessDashboard && (
                    <button onClick={() => { setNiOpen(false); navigate("/dashboard"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">For Dispatch</button>
                  )}
                </div>
              )}
            </div>
          )}
          {(canAccessMigration || canAccessMigrationDashboard) && (
            <div className="relative" ref={migrationRef}>
              <button
                onClick={() => setMigrationOpen((o) => !o)}
                className="bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm inline-flex items-center gap-1"
              >
                Migration
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {migrationOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white text-black rounded shadow-lg overflow-hidden z-50">
                  {canAccessMigrationDashboard && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/dispatch-dashboard"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm font-medium">Migration Dispatch</button>
                  )}
                  {hasPerm("migration.view_uploaded") && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/uploaded"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">Uploaded for Migration</button>
                  )}
                  {hasPerm("migration.view_calling") && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/for-calling"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">For Calling/Scheduling</button>
                  )}
                  {hasPerm("migration.view_dispatch") && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/for-dispatch"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">For Dispatch</button>
                  )}
                  {hasPerm("migration.view_visited") && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/visited-called"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm">Completed/Visited/Called</button>
                  )}
                  {canAccessMigrationDashboard && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/dashboard"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-t">Overall Migration Progress</button>
                  )}
                  {canAccessPublicMigrationRequests && (
                    <button onClick={() => { setMigrationOpen(false); navigate("/migration/public-requests"); }} className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-t">Public Migration Requests</button>
                  )}
                </div>
              )}
            </div>
          )}
          {canAccessReports && (
            <button
              onClick={() => navigateTo("/reports")}
              className="bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm"
            >
              Reports
            </button>
          )}
          {hasPerm("page.nap_locator") && (
            <button
              onClick={() => navigateTo("/nap-locator")}
              className="bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm"
            >
              NAP Locator
            </button>
          )}

          <NotificationBell />

          <div className="relative" ref={densityRef}>
            <button
              onClick={() => setDensityOpen((o) => !o)}
              className="bg-blue-800 hover:bg-blue-900 p-1.5 rounded inline-flex items-center justify-center"
              title={`View density: ${DENSITY_LABELS[density] || "Normal"}`}
              aria-label={`View density: ${DENSITY_LABELS[density] || "Normal"}`}
            >
              {/* Eye icon */}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            {densityOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-white text-black rounded shadow-lg overflow-hidden z-50">
                {[
                  { value: "normal", label: "Normal", sub: "100% — current view" },
                  { value: "compact", label: "Compact", sub: "90% — slightly denser" },
                  { value: "dense", label: "Dense", sub: "80% — more rows/cols" },
                  { value: "auto", label: "Auto", sub: "Adjust to screen size" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setDensity(opt.value); setDensityOpen(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center justify-between gap-2 ${density === opt.value ? "bg-blue-50" : ""}`}
                  >
                    <span>
                      <span className="font-medium block leading-tight">{opt.label}</span>
                      <span className="text-[11px] text-gray-500 block leading-tight">{opt.sub}</span>
                    </span>
                    {density === opt.value && (
                      <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="text-sm font-medium px-3 py-1 rounded bg-blue-800 hover:bg-blue-900"
            >
              {user?.display_name || user?.full_name}
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white text-black rounded shadow-lg overflow-hidden z-50">
                {canAccessManageAccounts && (
                  <button
                    onClick={() => navigateTo("/manage-accounts")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Manage Account
                  </button>
                )}
                {canViewRoles && (
                  <button
                    onClick={() => navigateTo("/roles")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Manage Roles
                  </button>
                )}
                {hasPerm("page.team_leaders") && (
                  <button
                    onClick={() => navigateTo("/team-leaders")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Team Leaders
                  </button>
                )}
                {hasPerm("page.teams") && (
                  <button
                    onClick={() => navigateTo("/teams")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Teams
                  </button>
                )}
                {hasPerm("page.companies") && (
                  <button
                    onClick={() => navigateTo("/companies")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Companies
                  </button>
                )}
                {canAccessDatabaseBackups && (
                  <button
                    onClick={() => navigateTo("/database-backups")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Backups
                  </button>
                )}
                {canAccessJoRegistry && ENABLE_JO_REGISTRY && (
                  <button
                    onClick={() => navigateTo("/jo-registry")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    JO Registry
                  </button>
                )}
                {canViewLoginActivity && (
                  <button
                    onClick={() => navigateTo("/login-activity")}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    Login Activity
                  </button>
                )}
                <button
                  onClick={() => { setMenuOpen(false); setShowChangePassword(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                  Change Password
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                  Logout
                </button>
                <div className="px-3 py-2 text-xs text-gray-400 border-t text-center select-none">
                  v{APP_VERSION}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
